import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FaRegUserCircle } from "react-icons/fa";
import Nav from "components/molecules/nav";
import adminApi from "services/admin-api";
import ConfirmModal from "components/global/ConfirmModal";
import SessionManagementModal from "components/global/SessionManagementModal";
import { SchoolSelector } from "components/global/SchoolSelector";
import { TeamSelector } from "components/global/TeamSelector";
import { PlayerSelector } from "components/global/PlayerSelector";
import {
  BreadcrumbEntityLabelProvider,
  useBreadcrumbEntityLabel,
} from "context/breadcrumb-entity-label-context";
import {
  SchoolOpsHeaderProvider,
  useSchoolOpsHeaderContext,
} from "context/school-ops-header-context";
import { useAuth } from "context/auth-context";
import { useSchoolBrandTheme } from "hooks/use-school-brand-theme";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import { AppBreadcrumb } from "components/layouts/app-layout/app-breadcrumb";
import { applyCurrentCrumbLabel, getBreadcrumbs } from "utils/breadcrumbs";
import {
  getPlayerIdFromWorkspacePath,
  getSchoolIdFromWorkspacePath,
  getTeamIdFromWorkspacePath,
  getWorkspaceSchoolEntityExitPath,
} from "utils/school-theme";
import { WorkspaceContextSelectorsRow } from "components/global/styles/SchoolSelector.inline.styles";
import {
  formatPortalLabel,
  getPortalSectionForPath,
  getWorkspaceSiblingTabs,
  isSchoolHubWorkspace,
} from "utils/portal-access";
import { getApiErrorMessage, getInitials } from "utils/admin";
import {
  DropdownAction,
  DropdownSurface,
  HeaderRow,
  HeaderStack,
  MainContent,
  ProfileToggle,
  SchoolHubSelectorCard,
  SchoolHubSelectorWrap,
  SchoolOpsHeaderCard,
  SchoolOpsPageActions,
  SchoolOpsPageTitle,
  SchoolOpsPageTitleRow,
  SchoolOpsTabsWrap,
  StickyHeaderWrap,
  SubTabButton,
  SubTabsCard,
  SubTabsCopy,
  SubTabsHeader,
  SubTabsRow,
  SubTabsScroller,
  SubTabsWrap,
} from "./styles/app-layout.styles";
import {
  AppLayoutSection,
  AppLayoutContentStack,
  AppLayoutFieldShell,
  AppLayoutFaChevronDown,
  AppLayoutThumbnailImage,
  AppLayoutLayoutColumn,
  AppLayoutLayoutShell,
  AppLayoutTextBlock,
  AppLayoutEyebrowText,
  AppLayoutEyebrowTextSecondary,
  AppLayoutProfileCopy,
  AppLayoutSkeletonLine,
  AppLayoutSkeletonLineSecondary,
  AppLayoutSkeletonAvatar,
} from "components/layouts/app-layout/styles/app-layout.inline.styles";
interface AppProps {
  children: React.ReactNode;
  route: string;
}

const ShellBreadcrumb: React.FC<{
  onNavigate: (href: string) => void;
  pathname: string;
}> = ({ onNavigate, pathname }) => {
  const { label } = useBreadcrumbEntityLabel();
  const items = useMemo(
    () => applyCurrentCrumbLabel(getBreadcrumbs(pathname), label),
    [label, pathname],
  );

  return <AppBreadcrumb items={items} onNavigate={onNavigate} />;
};

const SchoolOpsStickyHeaderRows: React.FC<{
  activePortalAccess: ReturnType<typeof useAuth>["portalAccess"];
  onSchoolChange?: (nextSchoolId: string) => void;
  routePlayerId: string | null;
  routeSchoolId: string | null;
  routeTeamId: string | null;
  showPlayerSelector: boolean;
  showSchoolSelector: boolean;
  showTeamSelector: boolean;
}> = ({
  activePortalAccess,
  onSchoolChange,
  routePlayerId,
  routeSchoolId,
  routeTeamId,
  showPlayerSelector,
  showSchoolSelector,
  showTeamSelector,
}) => {
  const { header } = useSchoolOpsHeaderContext();

  return (
    <>
      {showSchoolSelector ? (
        <SchoolHubSelectorWrap>
          <SchoolHubSelectorCard>
            <WorkspaceContextSelectorsRow>
              <SchoolSelector onSchoolChange={onSchoolChange} portalAccess={activePortalAccess} />
              {showTeamSelector && routeSchoolId && routeTeamId ? (
                <TeamSelector schoolId={routeSchoolId} teamId={routeTeamId} />
              ) : null}
              {showPlayerSelector && routeSchoolId && routePlayerId ? (
                <PlayerSelector playerId={routePlayerId} schoolId={routeSchoolId} />
              ) : null}
            </WorkspaceContextSelectorsRow>
          </SchoolHubSelectorCard>
        </SchoolHubSelectorWrap>
      ) : null}

      {header?.tabs ? (
        <SchoolOpsHeaderCard>
          <SchoolOpsTabsWrap>{header.tabs}</SchoolOpsTabsWrap>
        </SchoolOpsHeaderCard>
      ) : null}

      {header?.title ? (
        <SchoolOpsHeaderCard>
          <SchoolOpsPageTitleRow>
            <SchoolOpsPageTitle>{header.title}</SchoolOpsPageTitle>
            {header.actions ? <SchoolOpsPageActions>{header.actions}</SchoolOpsPageActions> : null}
          </SchoolOpsPageTitleRow>
        </SchoolOpsHeaderCard>
      ) : null}
    </>
  );
};

export const AppLayout: React.FC<AppProps> = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isSessionModalOpen, setSessionModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"current" | "all" | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const {
    activeSchoolId,
    logout,
    portalAccess,
    schoolThemePreference,
    setActiveSchoolId,
    setPortalAccess,
    setUserRole,
    userRole,
  } = useAuth();
  const { data: userData, isLoading } = useQuery(buildCurrentUserQueryOptions());
  useEffect(() => {
    const handleOpenSessions = () => setSessionModalOpen(true);
    window.addEventListener("athspire:open-sessions", handleOpenSessions);
    return () => window.removeEventListener("athspire:open-sessions", handleOpenSessions);
  }, []);
  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target;

      if (!(target instanceof Node) || profileMenuRef.current?.contains(target)) {
        return;
      }

      setDropdownOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDropdownOpen]);
  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!userData?.role) {
      return;
    }
    setUserRole(userData.role);
    setPortalAccess(userData.portal_access || null);
  }, [setPortalAccess, setUserRole, userData]);
  const activePortalAccess = userData?.portal_access || portalAccess;
  const activeRole = userData?.role || userRole || activePortalAccess?.primary_role || "user";
  const currentSection = getPortalSectionForPath(location.pathname);
  const scopedSchoolIds = useMemo(
    () => (activePortalAccess?.scope?.school_ids || []).map((schoolId) => String(schoolId)),
    [activePortalAccess?.scope?.school_ids],
  );
  const routeSchoolId = useMemo(
    () => getSchoolIdFromWorkspacePath(location.pathname),
    [location.pathname],
  );
  const routeTeamId = useMemo(
    () => getTeamIdFromWorkspacePath(location.pathname),
    [location.pathname],
  );
  const routePlayerId = useMemo(
    () => getPlayerIdFromWorkspacePath(location.pathname),
    [location.pathname],
  );
  const schoolEntityExitPath = useMemo(
    () => getWorkspaceSchoolEntityExitPath(location.pathname),
    [location.pathname],
  );
  const showTeamSelector = Boolean(routeSchoolId && routeTeamId);
  const showPlayerSelector = Boolean(routeSchoolId && routePlayerId);
  const handleSchoolContextChange = (nextSchoolId: string) => {
    if (!schoolEntityExitPath || !nextSchoolId) {
      return;
    }

    // Entity URLs lock school via routeSchoolId; leave the entity so the new school sticks.
    history.push(schoolEntityExitPath);
  };
  const isSchoolPortal = activePortalAccess?.portal_type === "school_management";
  const isSchoolSection =
    currentSection === "schools" ||
    currentSection === "player_performance_records" ||
    Boolean(currentSection?.startsWith("school_"));
  const themedSchoolId = routeSchoolId || activeSchoolId || scopedSchoolIds[0] || null;
  const shouldResolveSchoolTheme =
    schoolThemePreference === "school_brand" &&
    Boolean(themedSchoolId) &&
    (isSchoolPortal || isSchoolSection);
  const roleLabel = formatPortalLabel(activeRole);
  const workspaceTabState = useMemo(
    () => getWorkspaceSiblingTabs(activePortalAccess, currentSection),
    [activePortalAccess, currentSection],
  );
  const isSchoolHubWorkspaceActive = isSchoolHubWorkspace(currentSection, activePortalAccess);
  const showWorkspaceSubTabs = workspaceTabState.tabs.length > 1 && !isSchoolHubWorkspaceActive;
  const { data: themedSchool } = useQuery({
    queryKey: ["app-layout-themed-school", themedSchoolId],
    queryFn: () => adminApi.schools.get(String(themedSchoolId)),
    enabled: shouldResolveSchoolTheme,
  });
  useSchoolBrandTheme(shouldResolveSchoolTheme ? themedSchool?.branding : null, {
    enabled: shouldResolveSchoolTheme,
  });
  const closeSessionUi = () => {
    setConfirmAction(null);
    setSessionModalOpen(false);
    setDropdownOpen(false);
  };
  useEffect(() => {
    // Deep-link sync only: school entity routes carry a school id in the URL.
    // Sync when the URL school changes — do not snap back if the user changes
    // school while leaving the entity (selector + navigate to list).
    if (!routeSchoolId) {
      return;
    }

    const hasGlobalScope = Boolean(activePortalAccess?.scope?.global);
    const inScope =
      hasGlobalScope || !scopedSchoolIds.length || scopedSchoolIds.includes(routeSchoolId);

    if (!inScope) {
      return;
    }

    setActiveSchoolId(routeSchoolId);
  }, [activePortalAccess?.scope?.global, routeSchoolId, scopedSchoolIds, setActiveSchoolId]);
  const onClickProfile = () => {
    setDropdownOpen(false);
    history.push("/profile");
  };
  const logoutCurrentMutation = useMutation({
    mutationFn: () => adminApi.sessions.logoutCurrent(),
    onSuccess: () => {
      toast.success("Signed out on this device.");
      closeSessionUi();
      logout();
      history.push("/login");
    },
    onError: (error: unknown) => {
      toast.error(
        getApiErrorMessage(
          error,
          "Unable to sign out on this device cleanly. Clearing locally instead.",
        ),
      );
      closeSessionUi();
      logout();
      history.push("/login");
    },
  });
  const logoutAllMutation = useMutation({
    mutationFn: () => adminApi.sessions.logoutAll(),
    onSuccess: () => {
      toast.success("Signed out everywhere.");
      closeSessionUi();
      logout();
      history.push("/login");
    },
    onError: (error: unknown) => {
      toast.error(
        getApiErrorMessage(
          error,
          "Unable to sign out everywhere cleanly. Clearing locally instead.",
        ),
      );
      closeSessionUi();
      logout();
      history.push("/login");
    },
  });
  return (
    <AppLayoutLayoutShell>
      <Nav portalAccess={activePortalAccess} userRole={activeRole} />

      <AppLayoutLayoutColumn>
        <BreadcrumbEntityLabelProvider>
          <SchoolOpsHeaderProvider>
            <StickyHeaderWrap>
              <HeaderStack>
                <HeaderRow>
                  <ShellBreadcrumb
                    onNavigate={(href) => history.push(href)}
                    pathname={location.pathname}
                  />

                  <AppLayoutFieldShell ref={profileMenuRef}>
                    <ProfileToggle
                      aria-expanded={isDropdownOpen}
                      aria-haspopup="menu"
                      onClick={() => setDropdownOpen((prev) => !prev)}
                      type="button"
                    >
                      {isLoading ? (
                        <AppLayoutSkeletonAvatar />
                      ) : userData?.image ? (
                        <AppLayoutThumbnailImage
                          alt={userData.full_name || userData.name || "User"}
                          fallbackLabel={userData.full_name || userData.email}
                          src={userData.image}
                          variant="user"
                        />
                      ) : (
                        <AppLayoutSection>
                          {getInitials(userData?.full_name || userData?.name || "AU") || (
                            <FaRegUserCircle />
                          )}
                        </AppLayoutSection>
                      )}

                      <AppLayoutProfileCopy>
                        {isLoading ? (
                          <AppLayoutContentStack>
                            <AppLayoutSkeletonLine />
                            <AppLayoutSkeletonLineSecondary />
                          </AppLayoutContentStack>
                        ) : (
                          <>
                            <AppLayoutTextBlock>
                              {userData?.full_name || userData?.name || "Athspire user"}
                            </AppLayoutTextBlock>
                            <AppLayoutEyebrowText>{roleLabel}</AppLayoutEyebrowText>
                          </>
                        )}
                      </AppLayoutProfileCopy>
                      <AppLayoutFaChevronDown />
                    </ProfileToggle>

                    {isDropdownOpen ? (
                      <DropdownSurface role="menu">
                        <DropdownAction onClick={onClickProfile} type="button">
                          Profile
                        </DropdownAction>
                        <DropdownAction
                          onClick={() => {
                            setDropdownOpen(false);
                            setSessionModalOpen(true);
                          }}
                          type="button"
                        >
                          Signed-in devices
                        </DropdownAction>
                        <DropdownAction
                          $danger
                          onClick={() => {
                            setDropdownOpen(false);
                            setConfirmAction("current");
                          }}
                          type="button"
                        >
                          Log out
                        </DropdownAction>
                      </DropdownSurface>
                    ) : null}
                  </AppLayoutFieldShell>
                </HeaderRow>

                <SchoolOpsStickyHeaderRows
                  activePortalAccess={activePortalAccess}
                  onSchoolChange={handleSchoolContextChange}
                  routePlayerId={routePlayerId}
                  routeSchoolId={routeSchoolId}
                  routeTeamId={routeTeamId}
                  showPlayerSelector={showPlayerSelector}
                  showSchoolSelector={isSchoolHubWorkspaceActive}
                  showTeamSelector={showTeamSelector}
                />

                {showWorkspaceSubTabs ? (
                  <SubTabsWrap>
                    <SubTabsCard>
                      <SubTabsHeader>
                        <SubTabsCopy>
                          <AppLayoutEyebrowTextSecondary>
                            {workspaceTabState.group?.label || "Workspace"}
                          </AppLayoutEyebrowTextSecondary>
                        </SubTabsCopy>

                        <SubTabsScroller>
                          <SubTabsRow>
                            {workspaceTabState.tabs.map((tab) => {
                              const isActiveTab =
                                location.pathname === tab.path ||
                                location.pathname.startsWith(`${tab.path}/`);
                              return (
                                <SubTabButton
                                  $active={isActiveTab}
                                  key={tab.path}
                                  onClick={() => history.push(tab.path)}
                                  type="button"
                                >
                                  {tab.label}
                                </SubTabButton>
                              );
                            })}
                          </SubTabsRow>
                        </SubTabsScroller>
                      </SubTabsHeader>
                    </SubTabsCard>
                  </SubTabsWrap>
                ) : null}
              </HeaderStack>
            </StickyHeaderWrap>

            <MainContent>{children}</MainContent>
          </SchoolOpsHeaderProvider>
        </BreadcrumbEntityLabelProvider>
      </AppLayoutLayoutColumn>

      <SessionManagementModal
        isOpen={isSessionModalOpen}
        onClose={() => setSessionModalOpen(false)}
        onLogoutAllRequest={() => setConfirmAction("all")}
        onLogoutCurrentRequest={() => setConfirmAction("current")}
      />

      <ConfirmModal
        cancelLabel="Stay signed in"
        confirmLabel="Sign out"
        description="You will return to the login screen and be signed out on this device."
        isLoading={logoutCurrentMutation.isPending}
        isOpen={confirmAction === "current"}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => logoutCurrentMutation.mutate()}
        title="Sign out on this device?"
        tone="danger"
      />

      <ConfirmModal
        cancelLabel="Stay signed in"
        confirmLabel="Sign out everywhere"
        description="This signs you out on every device tied to your account, including this one."
        isLoading={logoutAllMutation.isPending}
        isOpen={confirmAction === "all"}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => logoutAllMutation.mutate()}
        title="Sign out everywhere?"
        tone="danger"
      />
    </AppLayoutLayoutShell>
  );
};
